import React from "react";
import { motion } from "framer-motion";
import { Play, ArrowUpRight } from "lucide-react";
import { Overline, WarliDivider } from "./Motifs";
import { PUBLIC_WORK, SITE } from "../data/content";

const TEDX_PHOTO =
  "https://customer-assets.emergentagent.com/job_meera-adventures/artifacts/rdtedlxh_ChatGPT%20Image%20Jun%2017%2C%202026%2C%2012_28_21%20AM.png";

const PAINTINGS = [
  {
    src: "https://customer-assets.emergentagent.com/job_meera-adventures/artifacts/eyoskuaj_ChatGPT%20Image%20Jun%2017%2C%202026%2C%2012_29_43%20AM.png",
    title: "Peacock in Gold",
    medium: "Acrylic & gold leaf on canvas",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_meera-adventures/artifacts/heegjbpr_ChatGPT%20Image%20Jun%2017%2C%202026%2C%2012_25_05%20AM.png",
    title: "Kathakali in Blue",
    medium: "Indigo & ink on canvas",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_meera-adventures/artifacts/vutv5fqt_ChatGPT%20Image%20Jun%2017%2C%202026%2C%2012_10_27%20AM.png",
    title: "Stallion",
    medium: "Oil impasto on canvas",
  },
];

export default function PublicWork() {
  return (
    <section
      id="public-work"
      data-testid="public-work-section"
      className="relative py-24 sm:py-32 bg-[#F3EDE4]/50 border-y border-[#E5DFD3]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Overline>Art, Education & Public Work</Overline>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1E2A4F] mt-5 leading-[0.95] tracking-tight">
              The work
              <br /> <em className="italic font-light">outside the books.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 text-[#5C6A8F] leading-relaxed">
            TEDx talks, exhibitions, research, and curriculum design — many threads that all
            pull toward the same question: how do we keep imagination alive?
          </div>
        </div>

        {/* TEDx feature */}
        <motion.a
          href={SITE.socials.tedx}
          target="_blank"
          rel="noreferrer"
          data-testid="tedx-card"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 group relative grid lg:grid-cols-12 gap-0 rounded-lg overflow-hidden border border-[#E5DFD3] bg-white"
        >
          <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto">
            <img
              src={TEDX_PHOTO}
              alt="Dr. Divya Ramachandran speaking at TEDx Bangalore — Stories. Soul. Impact."
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#1E2A4F]/70 via-[#1E2A4F]/20 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-20 h-20 rounded-full bg-[#D95D39] text-[#F9F6F0] inline-flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                <Play size={28} className="ml-1" />
              </span>
            </div>
          </div>
          <div className="lg:col-span-5 p-8 sm:p-12 flex flex-col justify-center">
            <Overline>TEDx Talk</Overline>
            <h3 className="font-display text-3xl sm:text-4xl text-[#1E2A4F] mt-4 leading-tight">
              Using Art to Ignite Ideas, Spur Creativity, and Spread Joy
            </h3>
            <p className="text-[#5C6A8F] mt-4 leading-relaxed">
              A 14-minute argument for putting art back at the centre of how we think, learn,
              and live together.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm text-[#D95D39] ink-link self-start">
              Watch on TED <ArrowUpRight size={14} />
            </span>
          </div>
        </motion.a>

        <WarliDivider className="w-full h-12 mt-16 text-[#1E2A4F]/40" />

        {/* Work list */}
        <div className="mt-16 grid lg:grid-cols-2 gap-x-12 gap-y-2">
          {PUBLIC_WORK.slice(1).map((w, i) => (
            <a
              key={w.title}
              href={w.href}
              target={w.href.startsWith("http") ? "_blank" : "_self"}
              rel="noreferrer"
              data-testid={`public-work-${i}`}
              className="group grid grid-cols-12 gap-4 py-6 border-b border-[#E5DFD3] hover:bg-white -mx-2 px-2 rounded transition-colors duration-300"
            >
              <span className="col-span-1 font-display text-[#D95D39] tabular-nums text-base">
                {String(i + 2).padStart(2, "0")}
              </span>
              <div className="col-span-10">
                <h4 className="font-display text-2xl text-[#1E2A4F] leading-tight group-hover:text-[#D95D39] transition-colors duration-500">
                  {w.title}
                </h4>
                <p className="text-sm text-[#5C6A8F] mt-1.5 leading-relaxed">{w.description}</p>
              </div>
              <ArrowUpRight
                size={18}
                className="col-span-1 text-[#1E2A4F] justify-self-end self-start mt-1 transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1"
              />
            </a>
          ))}
        </div>

        {/* Paintings gallery */}
        <div className="mt-20" data-testid="paintings-gallery">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <Overline>Paintings</Overline>
              <h3 className="font-display text-3xl sm:text-4xl text-[#1E2A4F] mt-3 leading-tight">
                Selected works on canvas.
              </h3>
            </div>
            <p className="text-sm text-[#5C6A8F] max-w-sm">
              Originals and prints available on request — write to me about commissions, sizes, and shipping.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            {PAINTINGS.map((p, i) => (
              <figure
                key={p.title}
                data-testid={`painting-${i}`}
                className="group rounded-lg overflow-hidden border border-[#E5DFD3] bg-white"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={p.src}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <figcaption className="px-5 py-4 border-t border-[#E5DFD3]">
                  <p className="font-display text-xl text-[#1E2A4F] leading-tight">{p.title}</p>
                  <p className="text-xs tracking-[0.2em] uppercase text-[#5C6A8F] mt-1.5">
                    {p.medium}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
